import { useState } from 'react';
import { ChevronDown, ChevronRight, AlertCircle, AlertTriangle, Info, Sparkles, CheckCircle2, ListChecks, Shield } from 'lucide-react';
import type { FullReport as FullReportType, ReportSection, BiomarkerResult } from '../../types';
import { MedicalDisclaimer } from '../shared/MedicalDisclaimer';

interface FullReportProps {
  report: FullReportType;
  biomarkers?: BiomarkerResult[];
}

// ---------------------------------------------------------------------------
// Severity helpers
// ---------------------------------------------------------------------------

const SEVERITY_STYLES: Record<ReportSection['severity'], { border: string; dot: string; text: string }> = {
  urgent: {
    border: 'border-l-red-400',
    dot: 'bg-red-400',
    text: 'text-red-400',
  },
  attention: {
    border: 'border-l-amber-400',
    dot: 'bg-amber-400',
    text: 'text-amber-400',
  },
  info: {
    border: 'border-l-blue-400',
    dot: 'bg-blue-400',
    text: 'text-blue-400',
  },
};

function SeverityIcon({ severity, size = 14 }: { severity: ReportSection['severity']; size?: number }) {
  const cls = SEVERITY_STYLES[severity].text;
  if (severity === 'urgent') return <AlertCircle size={size} className={cls} />;
  if (severity === 'attention') return <AlertTriangle size={size} className={cls} />;
  return <Info size={size} className={cls} />;
}

// ---------------------------------------------------------------------------
// Small reusable pieces
// ---------------------------------------------------------------------------

function BiomarkerTags({ names, biomarkers }: { names: string[]; biomarkers?: BiomarkerResult[] }) {
  if (names.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1.5 mt-3">
      {names.map((name) => {
        const match = biomarkers?.find((b) => b.biomarker === name);
        const statusColor = match
          ? match.hemologixStatus === 'optimal'
            ? 'bg-emerald-400/15 text-emerald-400 border-emerald-400/20'
            : match.hemologixStatus === 'critical'
              ? 'bg-red-400/15 text-red-400 border-red-400/20'
              : 'bg-amber-400/15 text-amber-400 border-amber-400/20'
          : 'bg-bg-tertiary text-text-muted border-bg-tertiary';

        return (
          <span
            key={name}
            className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${statusColor}`}
          >
            {name}
            {match && <span className="ml-1 opacity-70">{match.value} {match.unit}</span>}
          </span>
        );
      })}
    </div>
  );
}

function SectionNumber({ n }: { n: number }) {
  return (
    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-bg-tertiary flex items-center justify-center text-[11px] font-bold text-text-secondary">
      {n}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Section components
// ---------------------------------------------------------------------------

function KeyFindingCard({
  section,
  index,
  biomarkers,
}: {
  section: ReportSection;
  index: number;
  biomarkers?: BiomarkerResult[];
}) {
  const styles = SEVERITY_STYLES[section.severity];

  return (
    <div
      className={`bg-bg-card border border-bg-tertiary border-l-[3px] ${styles.border} rounded-xl px-4 py-3`}
    >
      <div className="flex items-start gap-3">
        <SectionNumber n={index + 1} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <SeverityIcon severity={section.severity} />
            <h4 className="text-sm font-semibold text-text-primary leading-snug">
              {section.title}
            </h4>
          </div>
          <p className="text-sm text-text-secondary leading-relaxed">{section.content}</p>
          <BiomarkerTags names={section.biomarkers} biomarkers={biomarkers} />
        </div>
      </div>
    </div>
  );
}

function CategorySection({
  section,
  expanded,
  onToggle,
  biomarkers,
}: {
  section: ReportSection;
  expanded: boolean;
  onToggle: () => void;
  biomarkers?: BiomarkerResult[];
}) {
  const styles = SEVERITY_STYLES[section.severity];
  const Chevron = expanded ? ChevronDown : ChevronRight;

  return (
    <div className="bg-bg-card border border-bg-tertiary rounded-xl overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-bg-elevated/40"
      >
        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${styles.dot}`} />
        <span className="flex-1 text-sm font-semibold text-text-primary">{section.title}</span>
        <Chevron size={16} className="text-text-muted flex-shrink-0" />
      </button>

      {expanded && (
        <div
          className="px-4 pb-4 border-t border-bg-tertiary"
          style={{ animation: 'fadeSlideIn 0.2s ease-out' }}
        >
          <p className="text-sm text-text-secondary leading-relaxed mt-3">{section.content}</p>
          <BiomarkerTags names={section.biomarkers} biomarkers={biomarkers} />
        </div>
      )}
    </div>
  );
}

function PatternCard({
  section,
  biomarkers,
}: {
  section: ReportSection;
  biomarkers?: BiomarkerResult[];
}) {
  return (
    <div className="bg-indigo-500/5 border border-indigo-400/20 rounded-xl px-4 py-3">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles size={14} className="text-indigo-400 flex-shrink-0" />
        <h4 className="text-sm font-semibold text-indigo-300">{section.title}</h4>
      </div>
      <p className="text-sm text-text-secondary leading-relaxed">{section.content}</p>
      <BiomarkerTags names={section.biomarkers} biomarkers={biomarkers} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function FullReportView({ report, biomarkers }: FullReportProps) {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

  const toggleSection = (title: string) => {
    setExpandedSections((prev) => ({ ...prev, [title]: !prev[title] }));
  };

  const formattedDate = (() => {
    try {
      return new Date(report.generatedAt).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return report.generatedAt;
    }
  })();

  return (
    <div
      className="min-h-screen bg-bg-deep text-text-primary"
      style={{ fontFamily: "'DM Sans', sans-serif" }}
    >
      {/* Inline keyframes for expand animation (no framer-motion) */}
      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(-4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* ---------------------------------------------------------------- */}
        {/* Header                                                           */}
        {/* ---------------------------------------------------------------- */}
        <header className="mb-6">
          <h1 className="font-display text-xl font-bold text-text-primary mb-1">
            Your Hemologix Lab Analysis
          </h1>
          <p className="text-xs text-text-muted">
            {formattedDate}
            <span className="mx-1.5 opacity-40">&middot;</span>
            {report.biomarkerCount} biomarkers
            <span className="mx-1.5 opacity-40">&middot;</span>
            <span className="text-amber-400">{report.flaggedCount} need attention</span>
          </p>
        </header>

        {/* ---------------------------------------------------------------- */}
        {/* Executive Summary                                                */}
        {/* ---------------------------------------------------------------- */}
        <section className="mb-8">
          <div className="bg-gold/5 border border-gold/30 rounded-xl px-4 py-4">
            <div className="flex items-center gap-2 mb-2">
              <Shield size={14} className="text-gold flex-shrink-0" />
              <h2 className="text-xs font-bold uppercase tracking-wider text-gold">
                Executive Summary
              </h2>
            </div>
            <p className="text-sm text-text-primary leading-relaxed">
              {report.executiveSummary}
            </p>
          </div>
        </section>

        {/* ---------------------------------------------------------------- */}
        {/* Key Findings                                                     */}
        {/* ---------------------------------------------------------------- */}
        {report.keyFindings.length > 0 && (
          <section className="mb-8">
            <h2 className="text-base font-bold text-text-primary mb-3 flex items-center gap-2">
              <AlertCircle size={16} className="text-text-secondary" />
              Key Findings
            </h2>
            <div className="flex flex-col gap-3">
              {report.keyFindings.map((finding, i) => (
                <KeyFindingCard
                  key={finding.title}
                  section={finding}
                  index={i}
                  biomarkers={biomarkers}
                />
              ))}
            </div>
          </section>
        )}

        {/* ---------------------------------------------------------------- */}
        {/* Category Deep Dives                                              */}
        {/* ---------------------------------------------------------------- */}
        {report.categoryAnalyses.length > 0 && (
          <section className="mb-8">
            <h2 className="text-base font-bold text-text-primary mb-3 flex items-center gap-2">
              <ListChecks size={16} className="text-text-secondary" />
              Category Deep Dives
            </h2>
            <div className="flex flex-col gap-2">
              {report.categoryAnalyses.map((section) => (
                <CategorySection
                  key={section.title}
                  section={section}
                  expanded={!!expandedSections[section.title]}
                  onToggle={() => toggleSection(section.title)}
                  biomarkers={biomarkers}
                />
              ))}
            </div>
          </section>
        )}

        {/* ---------------------------------------------------------------- */}
        {/* Pattern Connections                                              */}
        {/* ---------------------------------------------------------------- */}
        {report.patternConnections.length > 0 && (
          <section className="mb-8">
            <h2 className="text-base font-bold text-text-primary mb-3 flex items-center gap-2">
              <Sparkles size={16} className="text-indigo-400" />
              Pattern Connections
            </h2>
            <div className="flex flex-col gap-3">
              {report.patternConnections.map((pattern) => (
                <PatternCard
                  key={pattern.title}
                  section={pattern}
                  biomarkers={biomarkers}
                />
              ))}
            </div>
          </section>
        )}

        {/* ---------------------------------------------------------------- */}
        {/* Positive Findings                                                */}
        {/* ---------------------------------------------------------------- */}
        {report.positiveFindings.length > 0 && (
          <section className="mb-8">
            <h2 className="text-base font-bold text-text-primary mb-3 flex items-center gap-2">
              <CheckCircle2 size={16} className="text-emerald-400" />
              What Looks Good
            </h2>
            <div className="bg-emerald-400/5 border border-emerald-400/20 rounded-xl px-4 py-3">
              <ul className="flex flex-col gap-2">
                {report.positiveFindings.map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
                    <span className="text-sm text-text-secondary leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        {/* ---------------------------------------------------------------- */}
        {/* Next Steps                                                       */}
        {/* ---------------------------------------------------------------- */}
        {report.nextSteps.length > 0 && (
          <section className="mb-8">
            <h2 className="text-base font-bold text-text-primary mb-3 flex items-center gap-2">
              <ListChecks size={16} className="text-gold" />
              Next Steps
            </h2>
            <div className="bg-bg-card border border-bg-tertiary rounded-xl px-4 py-3">
              <ol className="flex flex-col gap-2.5">
                {report.nextSteps.map((step, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-gold/15 flex items-center justify-center text-[10px] font-bold text-gold">
                      {i + 1}
                    </span>
                    <span className="text-sm text-text-secondary leading-relaxed">{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          </section>
        )}

        {/* ---------------------------------------------------------------- */}
        {/* Footer / Disclaimer — Launch Blocker #12                         */}
        {/* ---------------------------------------------------------------- */}
        <MedicalDisclaimer className="mt-4 pb-8" />
      </div>
    </div>
  );
}
